/** The sign-in pages' form pieces, in the public site's design (styles/site.css, `.l-field`). */
import { forwardRef, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from "lucide-react";
import { cn } from "@client/lib/utils";

export function Field({ id, label, hint, children }: {
  id: string; label: string; hint?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-[var(--l-ink)]">{label}</label>
      {children}
      {hint}
    </div>
  );
}

export const TextField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextField({ className, ...props }, ref) {
    return <input ref={ref} className={cn("l-field", className)} {...props} />;
  },
);

/**
 * A password with the three things people get stuck on: seeing what they typed, knowing Caps Lock
 * is on (the classic "senha incorreta"), and — when creating one — how far from the minimum they are.
 */
export const PasswordField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & {
  /** Show the length meter against this minimum (creating a password). */
  minimum?: number;
}>(function PasswordField({ minimum, value, onKeyUp, onKeyDown, className, ...props }, ref) {
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const length = typeof value === "string" ? value.length : 0;
  const readCaps = (e: KeyboardEvent<HTMLInputElement>) => setCaps(e.getModifierState?.("CapsLock") ?? false);

  return (
    <div className="space-y-2">
      <div className="relative">
        <input ref={ref} type={show ? "text" : "password"} value={value}
               className={cn("l-field pr-12", className)}
               onKeyUp={(e) => { readCaps(e); onKeyUp?.(e); }}
               onKeyDown={(e) => { readCaps(e); onKeyDown?.(e); }}
               onBlur={() => setCaps(false)}
               {...props} />
        <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Ocultar senha" : "Mostrar senha"}
                className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[var(--l-ink-muted)] transition-colors hover:bg-[var(--l-paper)] hover:text-[var(--l-ink)]">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {caps && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700" role="status">
          <AlertCircle className="h-3.5 w-3.5" /> Caps Lock ligado
        </p>
      )}
      {minimum !== undefined && (
        <div aria-live="polite">
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--l-line)]">
            <div className={cn("h-full rounded-full transition-all duration-300", length >= minimum ? "bg-[var(--l-accent-ink)]" : "bg-amber-400")}
                 style={{ width: `${Math.min(100, (length / minimum) * 100)}%` }} />
          </div>
          <p className={cn("mt-1.5 flex items-center gap-1 text-xs", length >= minimum ? "font-medium text-[var(--l-accent-ink)]" : "text-[var(--l-ink-muted)]")}>
            {length >= minimum && <Check className="h-3.5 w-3.5" />}
            {length >= minimum ? "Tamanho certo" : `Pelo menos ${minimum} caracteres (faltam ${minimum - length})`}
          </p>
        </div>
      )}
    </div>
  );
});

export function SubmitButton({ busy, busyLabel, children, disabled }: {
  busy: boolean; busyLabel: string; children: ReactNode; disabled?: boolean;
}) {
  return (
    <button type="submit" disabled={busy || disabled}
            className="l-cta flex h-12 w-full items-center justify-center gap-2 rounded-full text-base font-bold disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none">
      {busy ? <><Loader2 className="l-spin h-4 w-4" /> {busyLabel}</> : children}
    </button>
  );
}

export function FormAlert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="flex items-start gap-2 rounded-xl bg-rose-50 px-3.5 py-3 text-sm font-medium text-rose-700">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {children}
    </p>
  );
}
